import { cssVar } from '@toeverything/theme';
import { cssVarV2 } from '@toeverything/theme/v2';
import { Component, lazy, type ReactNode, Suspense, useEffect } from 'react';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useNavigate,
} from 'react-router-dom';

import {
  StorefrontAuthProvider,
  useStorefrontAuth,
} from './auth/storefront-auth-provider';
import { Cart } from './screens/cart';
import { Catalog } from './screens/catalog';
import { ProductDetail } from './screens/product-detail';
import { StorefrontLayout } from './screens/storefront-layout';
import { CartProvider } from './services/cart-provider';
import { StorefrontApiProvider } from './services/storefront-api-provider';

// Off the public landing path (catalog/product/cart/layout stay EAGER), the
// buyer's own-orders + registration/verify screens are code-split — a shopper
// hitting the shop home never downloads them.
const Orders = lazy(() =>
  import('./screens/orders').then(m => ({ default: m.Orders }))
);
const Register = lazy(() =>
  import('./screens/register').then(m => ({ default: m.Register }))
);
const Verify = lazy(() =>
  import('./screens/verify').then(m => ({ default: m.Verify }))
);

/**
 * The blank token-themed interim panel: rendered while the AuthGuard's PKCE
 * redirect navigates away, and as the lazy-route Suspense fallback (same
 * existing loading style — no user-facing string to hardcode).
 */
function BlankScreen() {
  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: cssVarV2('layer/background/primary'),
        color: cssVarV2('text/primary'),
        fontFamily: cssVar('fontFamily'),
      }}
    />
  );
}

// One-shot chunk-reload recovery (the same pattern as the portal SPA — the two
// SPAs share no modules, so a small local copy each): a redeploy orphans the old
// hashed screen chunks, so the first lazy-route load failure reloads the page
// ONCE to pick up the new asset manifest. The sessionStorage flag arms the
// one-shot; a successful boot clears it (module top-level, once) so a future
// redeploy can recover again. The flag's boot-time value is captured BEFORE the
// clear — it is what the boundary consults, so a second consecutive failure
// (this boot IS the reload retry) renders the blank frame instead of looping.
const CHUNK_RELOAD_FLAG = 'pipe-chunk-reload';
const bootWasChunkReloadRetry = (() => {
  try {
    const was = sessionStorage.getItem(CHUNK_RELOAD_FLAG) !== null;
    sessionStorage.removeItem(CHUNK_RELOAD_FLAG);
    return was;
  } catch {
    return false;
  }
})();

/** The tiny shared boundary around each lazy route (no user-visible strings —
 *  it renders the existing {@link BlankScreen} while reloading / when the
 *  one-shot reload already ran). */
class ChunkErrorBoundary extends Component<
  { children: ReactNode },
  { failed: boolean }
> {
  override state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  override componentDidCatch() {
    if (!bootWasChunkReloadRetry) {
      try {
        sessionStorage.setItem(CHUNK_RELOAD_FLAG, '1');
      } catch {
        // sessionStorage unavailable — fall through to the blank frame.
      }
      window.location.reload();
    }
  }

  override render() {
    if (this.state.failed) {
      return <BlankScreen />;
    }
    return this.props.children;
  }
}

/**
 * Gate a screen behind a Bearer access token (mirrors the portal AuthGuard).
 * Unlike the portal, MOST storefront screens are public — only the buyer's
 * own-orders screen requires the session, so the guard wraps that route alone
 * (checkout inside the cart screen triggers login itself). With no token we
 * kick off the Keycloak Authorization-Code + PKCE redirect (CLAUDE.md #6).
 */
function AuthGuard({ children }: { children: ReactNode }) {
  const { token, login } = useStorefrontAuth();

  // No token → the Keycloak Authorization-Code + PKCE redirect. This navigates
  // away, so there is no interim screen (and no user-facing string to hardcode).
  useEffect(() => {
    if (!token) {
      // Best-effort: a failed redirect kickoff leaves the blank interim panel
      // (the console breadcrumb is the only trace — keep it).
      login().catch(e => console.error('storefront auth: pkce flow failed', e));
    }
  }, [token, login]);

  if (!token) {
    return <BlankScreen />;
  }

  return <>{children}</>;
}

/** Completes the `/callback` redirect, then returns to the shop home. */
function CallbackRoute() {
  const { handleCallback } = useStorefrontAuth();
  const navigate = useNavigate();

  useEffect(() => {
    handleCallback()
      .then(() => {
        navigate('/', { replace: true });
      })
      // Best-effort: a failed exchange stays on /callback (same as before —
      // the console breadcrumb is the only trace).
      .catch(e => console.error('storefront auth: pkce flow failed', e));
  }, [handleCallback, navigate]);

  return null;
}

/**
 * The storefront route table. The `/callback` route is intentionally OUTSIDE
 * the {@link AuthGuard} (it runs the token exchange). Public screens (catalog,
 * detail, cart, register, verify) render without a session; only the buyer's
 * own-orders screen is auth-gated.
 */
function StorefrontRoutes() {
  return (
    <Routes>
      <Route path="/callback" element={<CallbackRoute />} />
      <Route element={<StorefrontLayout />}>
        <Route path="/" element={<Catalog />} />
        <Route path="/products/:id" element={<ProductDetail />} />
        <Route path="/cart" element={<Cart />} />
        <Route
          path="/register"
          element={
            <ChunkErrorBoundary>
              <Suspense fallback={<BlankScreen />}>
                <Register />
              </Suspense>
            </ChunkErrorBoundary>
          }
        />
        <Route
          path="/verify"
          element={
            <ChunkErrorBoundary>
              <Suspense fallback={<BlankScreen />}>
                <Verify />
              </Suspense>
            </ChunkErrorBoundary>
          }
        />
        <Route
          path="/orders"
          element={
            <AuthGuard>
              <ChunkErrorBoundary>
                <Suspense fallback={<BlankScreen />}>
                  <Orders />
                </Suspense>
              </ChunkErrorBoundary>
            </AuthGuard>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

export const App = () => {
  return (
    <StorefrontAuthProvider>
      <StorefrontApiProvider>
        <CartProvider>
          <BrowserRouter>
            <StorefrontRoutes />
          </BrowserRouter>
        </CartProvider>
      </StorefrontApiProvider>
    </StorefrontAuthProvider>
  );
};
